/**
 * @file: MessagePayloadView.tsx
 *
 * Dispatches a "rich" chat payload to its dedicated renderer (in
 * `./renderers`). A strongly-typed `switch` on the payload's discriminant
 * narrows each case to the concrete variant — no `any`, no duck typing. Plain
 * text / notification payloads are handled by the bubble itself; this view
 * routes the media / audio / file / markdown / card kinds.
 *
 * `quoted` threads through to render every kind in its compact form, so the
 * whole dispatcher (or a single renderer) is reusable inside a reply preview.
 */

import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatMessagePayload } from '@euler/model/chat';
import { FC } from 'react';
import { AudioRenderer } from './renderers/AudioRenderer';
import { ContactCardRenderer } from './renderers/ContactCardRenderer';
import { FileRenderer } from './renderers/FileRenderer';
import { MarkdownRenderer } from './renderers/MarkdownRenderer';
import { MediaRenderer } from './renderers/MediaRenderer';
import { MediasRenderer } from './renderers/MediasRenderer';
import { ReportCardRenderer } from './renderers/ReportCardRenderer';
import { TaskCardRenderer } from './renderers/TaskCardRenderer';

/** Payload types that render via this view rather than the text bubble. */
export const RICH_PAYLOAD_TYPES = new Set([
  'media',
  'medias',
  'audio',
  'file',
  'markdown',
  'order_card',
  'service_record_card',
  'report_card',
  'contact_card',
]);

export function isRichPayloadType(type: string | undefined): boolean {
  return !!type && RICH_PAYLOAD_TYPES.has(type);
}

type Props = {
  payload: ChatMessagePayload;
  isMine: boolean;
  onPreviewImage?: (url: string) => void;
  onPreviewVideo?: (url: string) => void;
  onOpenUrl?: (url: string) => void;
  onOpenCard?: (payload: ChatMessagePayload) => void;
  /** Received audio not yet played → red unread dot. */
  audioUnread?: boolean;
  /** Fired when a received audio is first played (clears its dot). */
  onAudioPlay?: () => void;
  /** Render every kind in its compact quote-preview form. */
  quoted?: boolean;
  /** This message is a bot reply (renders suggested-question chips). */
  isBot?: boolean;
  /** Tapping a suggested-question chip. */
  onSuggestionPress?: (question: string) => void;
};

export const MessagePayloadView: FC<Props> = ({
  payload,
  isMine,
  onPreviewImage,
  onPreviewVideo,
  onOpenUrl,
  onOpenCard,
  audioUnread,
  onAudioPlay,
  quoted,
  isBot,
  onSuggestionPress,
}) => {
  const { rpx } = useResponsiveContext();

  switch (payload.type) {
    case 'media':
      return (
        <MediaRenderer
          payload={payload}
          isMine={isMine}
          quoted={quoted}
          onPreviewImage={onPreviewImage}
          onPreviewVideo={onPreviewVideo}
        />
      );

    case 'medias':
      return (
        <MediasRenderer
          payload={payload}
          isMine={isMine}
          quoted={quoted}
          onPreviewImage={onPreviewImage}
          onPreviewVideo={onPreviewVideo}
        />
      );

    case 'audio':
      return (
        <AudioRenderer
          payload={payload}
          isMine={isMine}
          quoted={quoted}
          audioUnread={audioUnread}
          onAudioPlay={onAudioPlay}
        />
      );

    case 'file':
      return (
        <FileRenderer
          payload={payload}
          isMine={isMine}
          quoted={quoted}
          onOpenUrl={onOpenUrl}
        />
      );

    case 'markdown':
      return (
        <MarkdownRenderer
          payload={payload}
          isMine={isMine}
          quoted={quoted}
          isBot={isBot}
          onSuggestionPress={onSuggestionPress}
        />
      );

    case 'contact_card':
      return (
        <ContactCardRenderer
          payload={payload}
          isMine={isMine}
          quoted={quoted}
          onOpenCard={onOpenCard}
        />
      );

    case 'order_card':
    case 'service_record_card':
      return (
        <TaskCardRenderer
          payload={payload}
          isMine={isMine}
          quoted={quoted}
          onOpenCard={onOpenCard}
        />
      );

    case 'report_card':
      return (
        <ReportCardRenderer
          payload={payload}
          isMine={isMine}
          quoted={quoted}
          onOpenCard={onOpenCard}
        />
      );

    default:
      return (
        <Typography.Text size={rpx(13)} color="#94A3B8">
          [{payload.type}]
        </Typography.Text>
      );
  }
};
